import * as THREE from 'three';
import { StarData, StarPosition } from 'types/galaxy';
import { VIEW_MODE_CALCULATORS } from 'utils/galaxy';
import { easeInOutCubic } from 'utils/galaxy/interpolation';

import { EngineConfig, EngineStar, ViewMode } from '../types';

const VIEW_TRANSITION_SPEED = 0.012;

// Vertex Shader (from original app.js:2480)
// uGpuAnim=1: Y-animation on GPU (non-spiral), uGpuAnim=0: positions from CPU (spiral)
const vertexShader = `
  attribute float size;
  attribute vec3 customColor;
  attribute float baseY;
  attribute float starIndex;
  attribute float aHighlight;
  uniform float uTime;
  uniform float uGpuAnim;
  uniform float uDimActive;
  uniform float uSizeMul;
  varying vec3 vColor;
  varying float vHl;

  void main() {
    vColor = customColor;
    vHl = aHighlight;
    vec3 pos = position;
    if (uGpuAnim > 0.5) {
      pos.y = baseY + sin(uTime * 0.2 + starIndex * 0.01) * 0.3;
    }
    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    // In highlight mode: selected stars grow, the rest shrink slightly
    float sizeK = mix(1.0, mix(0.85, 1.7, aHighlight), uDimActive);
    gl_PointSize = min(size * sizeK * uSizeMul * (200.0 / max(-mvPosition.z, 1.0)), 128.0);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

// Fragment Shader (matches original app.js:2491).
// depthCoreOnly: when 1.0, discard glow/halo fragments so only star cores write depth
// (used in depth-restore pass so logo is occluded only by star cores, not halos).
const fragmentShader = `
  uniform float depthCoreOnly;
  uniform float uDimActive;
  varying vec3 vColor;
  varying float vHl;

  void main() {
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);
    if (dist > 0.5) discard;
    if (depthCoreOnly > 0.5 && dist > 0.12) discard;

    float core = 1.0 - smoothstep(0.0, 0.2, dist);
    float glow = exp(-dist * dist * 18.0);
    float halo = exp(-dist * 4.5) * 0.15;

    vec3 finalColor = vColor * (core * 0.45 + glow * 0.35) + vColor * halo;
    float alpha = core * 0.45 + glow * 0.25 + halo;

    // Highlight mode: non-selected stars fade to a faint backdrop
    float dimK = mix(1.0, mix(0.055, 1.0, vHl), uDimActive);

    gl_FragColor = vec4(finalColor * dimK, alpha * dimK);
  }
`;

/**
 * Core system: star rendering, view mode transitions, animation.
 * Ported from: useStarsRenderer, useStarsAnimation, useViewModeCalculator, useViewTransition.
 *
 * All position data lives in Float32Array — zero React re-renders, zero GC pressure.
 */
export class StarsSystem {
  private points: THREE.Points | null = null;
  private geometry: THREE.BufferGeometry | null = null;
  private material: THREE.ShaderMaterial | null = null;

  // Star data (set once after API load)
  private stars: EngineStar[] = [];

  // Positions: current geometry positions are the source of truth.
  // During transition: fromPositions → toPositions (separate Float32Arrays).
  private fromPositions: Float32Array | null = null;
  private toPositions: Float32Array | null = null;

  // View transition state
  private transitioning = false;
  private transitionProgress = 0;
  private currentViewMode: ViewMode = 'spiral';

  private elapsed = 0;
  private smoothRotation = false;

  constructor(
    private scene: THREE.Scene,
    private config: EngineConfig
  ) {}

  /** Called ONCE after API data is loaded */
  setStars(stars: EngineStar[]): void {
    this.stars = stars;
    this.createPoints();
  }

  /** Switch view mode — starts transition */
  switchView(mode: ViewMode): void {
    if (mode === this.currentViewMode) return;
    if (this.stars.length === 0 || !this.geometry) return;

    // Mind view packs stars into dense behavioral clusters — shrink points
    // there so constellations stay resolved instead of blooming into blobs.
    if (this.material) {
      this.material.uniforms.uSizeMul.value = mode === 'mind' ? 0.45 : 1.0;
    }

    // Bake current group rotation.y into individual positions before snapshotting.
    // Non-spiral modes accumulate rotation.y on the Points group.
    // We must fold that into per-star positions so the "from" snapshot is visually correct
    // and then reset rotation.y to 0 — otherwise the transition would jump.
    this.bakeGroupRotation();

    // For spiral: restore original spiral data so calculateTargetPositions returns
    // the correct spiral positions (not overwritten tiers/other values).
    // Matches original app.js finalizeViewTransition() which restores spiralDistance etc.
    if (mode === 'spiral') {
      for (const star of this.stars) {
        const sd = star.spiralData;
        sd.angle = sd.origAngle;
        sd.distance = sd.origDistance;
        sd.baseY = sd.origBaseY;
        sd.rotationSpeed = sd.origRotationSpeed;
      }
    }

    // Snapshot current positions as "from"
    const pos = this.geometry.attributes.position.array as Float32Array;
    this.fromPositions = new Float32Array(pos);

    // Calculate target positions
    this.toPositions = this.calculateTargetPositions(mode);

    // Update baseY attribute for GPU animation after transition
    this.updateBaseY(this.toPositions);

    this.transitioning = true;
    this.transitionProgress = 0;
    this.currentViewMode = mode;
  }

  /** Called every frame from AnimationLoop */
  animate(dt: number): void {
    if (!this.points || this.stars.length === 0) return;

    this.elapsed += dt;

    if (this.transitioning) {
      this.animateTransition(dt);
    } else {
      this.animateCurrentMode(dt);
    }
  }

  setSmoothRotation(enabled: boolean): void {
    this.smoothRotation = enabled;
  }

  /**
   * Highlight a subset of stars by index; everything else dims to a faint
   * backdrop (uDimActive drives the shader). Pass null/empty to clear.
   */
  setHighlight(indices: number[] | null): void {
    if (!this.geometry || !this.material) return;
    const attr = this.geometry.attributes.aHighlight as THREE.BufferAttribute | undefined;
    if (!attr) return;

    const arr = attr.array as Float32Array;
    if (!indices || indices.length === 0) {
      arr.fill(1);
      this.material.uniforms.uDimActive.value = 0.0;
    } else {
      arr.fill(0);
      for (const i of indices) {
        if (i >= 0 && i < arr.length) arr[i] = 1;
      }
      this.material.uniforms.uDimActive.value = 1.0;
    }
    attr.needsUpdate = true;
  }

  getPoints(): THREE.Points | null {
    return this.points;
  }

  getMaterial(): THREE.ShaderMaterial | null {
    return this.material;
  }

  getStars(): EngineStar[] {
    return this.stars;
  }

  getCurrentViewMode(): ViewMode {
    return this.currentViewMode;
  }

  getTransitionProgress(): number {
    return this.transitionProgress;
  }

  isTransitioning(): boolean {
    return this.transitioning;
  }

  /** Get the current rotation.y of the points group (for syncing nebula) */
  getRotationY(): number {
    return this.points ? this.points.rotation.y : 0;
  }

  destroy(): void {
    if (this.points) {
      this.scene.remove(this.points);
      this.geometry?.dispose();
      this.material?.dispose();
      this.points = null;
      this.geometry = null;
      this.material = null;
    }
    this.fromPositions = null;
    this.toPositions = null;
  }

  // --- Private ---

  private createPoints(): void {
    const count = this.stars.length;
    if (count === 0) return;

    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const baseYArr = new Float32Array(count);
    const starIndexArr = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      const star = this.stars[i];
      positions[i * 3] = star.position.x;
      positions[i * 3 + 1] = star.position.y;
      positions[i * 3 + 2] = star.position.z;

      colors[i * 3] = star.color.r;
      colors[i * 3 + 1] = star.color.g;
      colors[i * 3 + 2] = star.color.b;

      sizes[i] = star.size;
      baseYArr[i] = star.position.y;
      starIndexArr[i] = i;
    }

    const highlightArr = new Float32Array(count).fill(1);

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('customColor', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute('baseY', new THREE.BufferAttribute(baseYArr, 1));
    geometry.setAttribute('starIndex', new THREE.BufferAttribute(starIndexArr, 1));
    geometry.setAttribute('aHighlight', new THREE.BufferAttribute(highlightArr, 1));

    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTime: { value: 0.0 },
        uGpuAnim: { value: 0.0 },
        depthCoreOnly: { value: 0.0 },
        uDimActive: { value: 0.0 },
        uSizeMul: { value: 1.0 },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    this.geometry = geometry;
    this.material = material;
    this.points = new THREE.Points(geometry, material);
    this.points.frustumCulled = false; // Bounding sphere may be stale after view switches
    this.scene.add(this.points);
  }

  private animateTransition(dt: number): void {
    this.transitionProgress += VIEW_TRANSITION_SPEED * dt * 60;

    if (this.transitionProgress >= 1) {
      this.transitionProgress = 1;
      this.transitioning = false;
    }

    const t = easeInOutCubic(this.transitionProgress);
    const pos = this.geometry!.attributes.position.array as Float32Array;
    const from = this.fromPositions!;
    const to = this.toPositions!;
    const len = this.stars.length;

    // Direct Float32Array write — zero allocations
    for (let i = 0; i < len; i++) {
      const i3 = i * 3;
      pos[i3] = from[i3] + (to[i3] - from[i3]) * t;
      pos[i3 + 1] = from[i3 + 1] + (to[i3 + 1] - from[i3 + 1]) * t;
      pos[i3 + 2] = from[i3 + 2] + (to[i3 + 2] - from[i3 + 2]) * t;
    }
    this.geometry!.attributes.position.needsUpdate = true;

    // Disable GPU Y-anim and group rotation during transition
    this.material!.uniforms.uGpuAnim.value = 0.0;

    if (!this.transitioning) {
      this.finalizeTransition();
    }
  }

  private finalizeTransition(): void {
    this.fromPositions = null;
    this.toPositions = null;

    const pos = this.geometry!.attributes.position.array as Float32Array;

    if (this.currentViewMode === 'spiral') {
      // Restore original spiral orbital data (matching original app.js finalizeViewTransition):
      // distance/baseY/rotationSpeed from saved originals, angle recalculated from final position.
      for (let i = 0; i < this.stars.length; i++) {
        const sd = this.stars[i].spiralData;
        const x = pos[i * 3];
        const z = pos[i * 3 + 2];
        sd.angle = Math.atan2(z, x); // ≈ origAngle (final pos is the spiral target)
        sd.distance = sd.origDistance;
        sd.baseY = sd.origBaseY;
        sd.rotationSpeed = sd.origRotationSpeed;
      }
    } else if (this.currentViewMode === 'tiers') {
      // Tiers uses its own orbital data calculated from final positions.
      // Different rotation speed: 0.001/sqrt(...) vs spiral's 0.0015/sqrt(...).
      // Matching original app.js finalizeViewTransition() tiers branch.
      for (let i = 0; i < this.stars.length; i++) {
        const sd = this.stars[i].spiralData;
        const x = pos[i * 3];
        const z = pos[i * 3 + 2];
        sd.distance = Math.sqrt(x * x + z * z);
        sd.angle = Math.atan2(z, x);
        sd.baseY = pos[i * 3 + 1];
        sd.rotationSpeed = 0.001 / Math.sqrt(0.3 + sd.distance / this.config.galaxyRadius);
      }
    }
  }

  /** Fold accumulated group rotation.y into per-star positions, then reset to 0 */
  private bakeGroupRotation(): void {
    if (!this.points || !this.geometry) return;

    const rotY = this.points.rotation.y;
    if (Math.abs(rotY) < 0.001) {
      this.points.rotation.y = 0;
      return;
    }

    const cosR = Math.cos(rotY);
    const sinR = Math.sin(rotY);
    const pos = this.geometry.attributes.position.array as Float32Array;
    const len = this.stars.length;

    for (let i = 0; i < len; i++) {
      const i3 = i * 3;
      const x = pos[i3];
      const z = pos[i3 + 2];
      pos[i3] = x * cosR + z * sinR;
      pos[i3 + 2] = -x * sinR + z * cosR;
    }
    this.geometry.attributes.position.needsUpdate = true;
    this.points.rotation.y = 0;
  }

  private animateCurrentMode(dt: number): void {
    const elapsed = this.elapsed;
    // Per-frame rotation multiplier — matches original app.js line 4845.
    // Mobile gets 0.5x speed to compensate for lower GPU performance.
    const isMobile =
      window.innerWidth <= 768 || (window.innerHeight <= 500 && window.innerWidth <= 1024);
    const rotationMultiplier = isMobile ? 0.5 : 1.0;
    // In pilot mode: normalize by dt so rotation is frame-rate independent (smooth at any FPS).
    // In other modes: keep original per-frame increment to match original behaviour.
    const step = this.smoothRotation ? dt * 60 : 1.0;

    if (this.currentViewMode === 'spiral' || this.currentViewMode === 'tiers') {
      // CPU animation — per-star orbital rotation (spiral & tier rings).
      this.material!.uniforms.uGpuAnim.value = 0.0;
      const positions = this.geometry!.attributes.position.array as Float32Array;

      for (let i = 0; i < this.stars.length; i++) {
        const sd = this.stars[i].spiralData;
        sd.angle += sd.rotationSpeed * rotationMultiplier * step;
        positions[i * 3] = Math.cos(sd.angle) * sd.distance;
        positions[i * 3 + 2] = Math.sin(sd.angle) * sd.distance;
        positions[i * 3 + 1] = sd.baseY + Math.sin(elapsed * 0.3 + sd.angle) * 0.2;
      }
      this.geometry!.attributes.position.needsUpdate = true;
    } else {
      // GPU animation — Y-offset in vertex shader, group rotation.
      this.material!.uniforms.uGpuAnim.value = 1.0;
      this.material!.uniforms.uTime.value = elapsed;
      this.points!.rotation.y += 0.0003 * rotationMultiplier * step;
    }
  }

  /** Convert view calculator output (StarPosition[]) to Float32Array */
  private calculateTargetPositions(mode: ViewMode): Float32Array {
    // View mode calculators expect StarData[] — adapt EngineStar[] to StarData[]
    const starsAsStarData = this.stars as unknown as StarData[];
    const calculator = VIEW_MODE_CALCULATORS[mode];
    const positions: StarPosition[] = calculator(starsAsStarData, this.config as never);

    const result = new Float32Array(this.stars.length * 3);
    for (let i = 0; i < positions.length; i++) {
      result[i * 3] = positions[i].x;
      result[i * 3 + 1] = positions[i].y;
      result[i * 3 + 2] = positions[i].z;
    }
    return result;
  }

  /** Update the baseY attribute for GPU Y-animation after transition */
  private updateBaseY(targetPositions: Float32Array): void {
    if (!this.geometry) return;
    const baseY = this.geometry.attributes.baseY.array as Float32Array;
    for (let i = 0; i < this.stars.length; i++) {
      baseY[i] = targetPositions[i * 3 + 1];
    }
    this.geometry.attributes.baseY.needsUpdate = true;
  }
}
